// The YouTube IFrame Player API (https://developers.google.com/youtube/iframe_api_reference), loaded on first
// entry into a song's practice videos (GitHub issue #4041). Only what the practice video player uses is typed
// here, rather than adding @types/youtube.

// the embed host: YouTube's privacy-enhanced mode, which sets no cookies until the video is played
export const youTubeEmbedHost = 'https://www.youtube-nocookie.com';

const youTubeIframeApiUrl = 'https://www.youtube.com/iframe_api';

// YT.PlayerState
export const YouTubePlayerState = {
  Unstarted: -1,
  Ended: 0,
  Playing: 1,
  Paused: 2,
  Buffering: 3,
  Cued: 5,
} as const;

export interface YouTubePlayer {
  playVideo(): void;
  pauseVideo(): void;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  getCurrentTime(): number;
  getDuration(): number;
  getPlayerState(): number;
  setPlaybackRate(rate: number): void;
  getPlaybackRate(): number;
  getAvailablePlaybackRates(): number[];
  loadVideoById(videoId: string, startSeconds?: number): void;
  cueVideoById(videoId: string, startSeconds?: number): void;
  // not in YouTube's reference, but long part of the player: the loaded video's title and channel. Undocumented,
  // so unguaranteed too: a production state change found it returning undefined (GitHub issue #4087)
  getVideoData(): YouTubeVideoData | undefined;
  destroy(): void;
}

export interface YouTubeVideoData {
  title: string;
  // the channel's name
  author: string;
  video_id: string;
}

export interface YouTubePlayerEvent {
  target: YouTubePlayer;
}

// `data` is a YouTubePlayerState value for onStateChange and YouTube's error code for onError
export interface YouTubePlayerDataEvent extends YouTubePlayerEvent {
  data: number;
}

export interface YouTubePlayerOptions {
  host?: string;
  videoId?: string;
  width?: number | string;
  height?: number | string;
  playerVars?: {
    playsinline?: 0 | 1;
    rel?: 0 | 1;
    controls?: 0 | 1;
    enablejsapi?: 0 | 1;
    iv_load_policy?: 1 | 3;
    origin?: string;
  };
  events?: {
    onReady?: (event: YouTubePlayerEvent) => void;
    onStateChange?: (event: YouTubePlayerDataEvent) => void;
    onError?: (event: YouTubePlayerDataEvent) => void;
  };
}

// the global `YT`
export interface YouTubeNamespace {
  // YouTube replaces `target` with its iframe
  Player: new (target: HTMLElement, options: YouTubePlayerOptions) => YouTubePlayer;
  PlayerState: typeof YouTubePlayerState;
}

declare global {
  interface Window {
    // the script sets `{ loading: 1, loaded: 0 }` first and adds Player once its widget script has loaded
    YT?: Partial<YouTubeNamespace> & { loading?: number; loaded?: number };
    onYouTubeIframeAPIReady?: () => void;
  }
}

// how long the API may take to say it is ready once its script is requested: the script can load and still never
// call back (its widget script blocked by a privacy blocker, the connection lost mid-way)
const loadTimeoutMs = 15_000;

let loading: Promise<YouTubeNamespace> | undefined;

/**
 * The YouTube IFrame Player API, loading its script on the first call. A failed load (offline, or no ready
 * call within `timeoutMs`) is forgotten, so the next call tries again.
 */
export function loadYouTubeIframeApi({
  timeoutMs = loadTimeoutMs,
}: { timeoutMs?: number } = {}): Promise<YouTubeNamespace> {
  if (window.YT?.Player) {
    return Promise.resolve(window.YT as YouTubeNamespace);
  }
  if (loading) {
    return loading;
  }
  loading = new Promise<YouTubeNamespace>((resolve, reject) => {
    // the script calls this global once YT.Player exists
    const previousReady = window.onYouTubeIframeAPIReady;
    const script = document.createElement('script');
    const fail = (message: string) => {
      clearTimeout(timeout);
      script.remove();
      // The script guards on the `YT.loading` flag it set, so a fresh copy of it would do nothing while this
      // half-loaded `YT` remains: the next call could never succeed
      if (!window.YT?.Player) {
        delete window.YT;
      }
      window.onYouTubeIframeAPIReady = previousReady;
      loading = undefined;
      reject(new Error(message));
    };
    const timeout = setTimeout(() => fail(`${youTubeIframeApiUrl} was not ready after ${timeoutMs} ms`), timeoutMs);
    window.onYouTubeIframeAPIReady = () => {
      previousReady?.();
      if (window.YT?.Player) {
        clearTimeout(timeout);
        // from here on window.YT answers at once
        loading = undefined;
        resolve(window.YT as YouTubeNamespace);
      }
    };
    script.src = youTubeIframeApiUrl;
    script.async = true;
    script.addEventListener('error', () => fail(`Failed to load ${youTubeIframeApiUrl}`));
    document.head.appendChild(script);
  });
  return loading;
}
